import EntryGrid from 'components/Inputs/EntryGrid';
import React from 'react';
import { Row, Col, FormGroup, Button } from 'reactstrap';
import TextBox from 'components/Inputs/TextBox';
import { t } from 'i18next';
import { useFieldArray } from 'react-hook-form';

function ItemInfoForm({ register, errors, control, editData, getValues, watch, setValue }) {
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Auto-calculate fields when values change
  const calculateItemValues = (index) => {
    const items = getValues('items');
    const item = items[index];
    
    if (!item) return;
    
    const taxAmount = parseFloat(item.tax_amount_excl_tax) || 0;
    const docServiceFee = parseFloat(item.doc_service_fee) || 0;
    
    // Formula: value + tax + service_fee = total_amount
    const totalAmount =  taxAmount + docServiceFee;
    setValue(`items.${index}.total_amount`, totalAmount.toFixed(2));
  };

  React.useEffect(() => {
    // Initialize with one empty item if no fields exist
    if (fields.length === 0 && !editData) {
      append({
        commodity: '',
        unit: '',
        qty: 1,
        import_licence_commodity_value_unit: 0,
        tax_amount_excl_tax: 0,
        doc_service_fee: 0,
        total_amount: 0,
        remark: '',
      });
    }
  }, []);

  // Watch all items fields for changes
  const watchedItems = watch('items');

  React.useEffect(() => {
    // Recalculate all items whenever any field changes
    if (watchedItems && watchedItems.length > 0) {
      watchedItems.forEach((item, index) => {
        if (item) {
          calculateItemValues(index);
        }
      });
    }
  }, [watchedItems]);

  const handleAddItem = () => {
    append({
      commodity: '',
      unit: '',
      qty: 1,
      import_licence_commodity_value_unit: 0,
      tax_amount_excl_tax: 0,
      doc_service_fee: 0,
      total_amount: 0,
      remark: '',
    });
  };

  const handleRemoveItem = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <EntryGrid 
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>Item Information</span>
          <Button 
            color='primary' 
            size='sm' 
            type='button'
            onClick={handleAddItem}
          >
            <i className='fa fa-plus mr-2' />
            {t('add')}
          </Button>
        </div>
      }
    >
      {fields.map((field, index) => (
        <div key={field.id} style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: index < fields.length - 1 ? '2px solid #e0e0e0' : 'none' }}>
          {/* Row 1: Commodity, Unit, QTY */}
          <Row className='pt-1 pb-1'>
            <Col md={4} sm={12}>
              <FormGroup>
                <label>Commodity</label>
                <TextBox
                  className='form-control mr-2'
                  type='text'
                  registerProps={register(`items.${index}.commodity`, {
                    required: 'Commodity is required',
                  })}
                  errors={errors?.items?.[index]?.commodity}
                />
              </FormGroup>
            </Col>
            <Col md={2} sm={6}>
              <FormGroup>
                <label>Unit</label>
                <TextBox
                  className='form-control mr-2'
                  type='text'
                  registerProps={register(`items.${index}.unit`, {
                    required: 'Unit is required',
                  })}
                  errors={errors?.items?.[index]?.unit}
                />
              </FormGroup>
            </Col>
            
            <Col md={2} sm={6}>
              <FormGroup>
                <label>QTY</label>
                <TextBox
                  className='form-control mr-2'
                  type='number'
                  registerProps={register(`items.${index}.qty`, {
                    required: 'QTY is required',
                    valueAsNumber: true,
                  })}
                  errors={errors?.items?.[index]?.qty}
                  defaultValue={1}
                  onChange={(e) => {
                    setValue(`items.${index}.qty`,e.target.value);
                    calculateItemValues(index);
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2} sm={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <FormGroup style={{ marginBottom: '1rem' }}>
                <Button
                  color='danger'
                  size='sm'
                  type='button'
                  onClick={() => handleRemoveItem(index)}
                  disabled={fields.length === 1}
                  title={fields.length === 1 ? 'At least one item is required' : 'Remove item'}
                >
                  <i className='fa fa-trash' />
                </Button>
              </FormGroup>
            </Col>
          </Row>

          {/* Row 2: Import Licence Value/Unit, Tax Amount, Doc/Service Fee, Total Amount */}
          <Row className='pt-1 pb-1'>
            <Col md={3} sm={6}>
              <FormGroup>
                <label>Import Licence Commodity Value/Unit</label>
                <TextBox
                  className='form-control mr-2'
                  type='number'
                  registerProps={register(`items.${index}.import_licence_commodity_value_unit`, {
                    valueAsNumber: true,
                  })}
                  errors={errors?.items?.[index]?.import_licence_commodity_value_unit}
                  defaultValue={0}
                  onChange={(e) => {
                    setValue(`items.${index}.import_licence_commodity_value_unit`, parseFloat(e.target.value) || 0);
                    calculateItemValues(index);
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3} sm={6}>
              <FormGroup>
                <label>Tax Amount (Excl Tax)</label>
                <TextBox
                  className='form-control mr-2'
                  type='number'
                  registerProps={register(`items.${index}.tax_amount_excl_tax`, {
                    valueAsNumber: true,
                  })}
                  errors={errors?.items?.[index]?.tax_amount_excl_tax}
                  defaultValue={0}
                  onChange={(e) => {
                    setValue(`items.${index}.tax_amount_excl_tax`, parseFloat(e.target.value) || 0);
                    calculateItemValues(index);
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3} sm={6}>
              <FormGroup>
                <label>Document/Service Fee</label>
                <TextBox
                  className='form-control mr-2'
                  type='number'
                  registerProps={register(`items.${index}.doc_service_fee`, {
                    valueAsNumber: true,
                  })}
                  errors={errors?.items?.[index]?.doc_service_fee}
                  defaultValue={0}
                  onChange={(e) => {
                    setValue(`items.${index}.doc_service_fee`, parseFloat(e.target.value) || 0);
                    calculateItemValues(index);
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={3} sm={6}>
              <FormGroup>
                <label>Total Amount (MMK)</label>
                <TextBox
                  className='form-control mr-2'
                  type='number'
                  registerProps={register(`items.${index}.total_amount`)}
                  errors={errors?.items?.[index]?.total_amount}
                  value={watch(`items.${index}.total_amount`) || 0}
                  disabled
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Row 3: Remark */}
          <Row className='pt-1 pb-1'>
            <Col md={12} sm={12}>
              <FormGroup>
                <label>Remark</label>
                <TextBox
                  className='form-control mr-2'
                  type='text'
                  registerProps={register(`items.${index}.remark`)}
                  errors={errors?.items?.[index]?.remark}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      ))}
    </EntryGrid>
  );
}
export default ItemInfoForm;
